import { AccountData, Event, EventType, ThemeType } from "@markit/common.types";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useWindowResize } from "../../../../hooks/useWindowResize";
import { checkIsDesktop } from "../../../../utils/screenResponsivenessUtils";
import { isDesktop } from "react-device-detect";
import FullProductHeaderSection from "./FullProductHeaderSection";
import MediaPlayer from "../Components/MediaPlayer";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import PDFViewer from "../Components/PDFViewer";
import MediaPreviewCarousel from "../../../Media/MediaPreviewCarousel";

type FullProductContentProps = {
  product: Event;
  creatorData: AccountData;
  hardTheme: ThemeType;
  hiddenModalComp?: ReactNode; // To have it absolute positioned to the actual content
  renderCheckoutComp?: ReactNode;
  aboveBodyComp?: ReactNode; // Rendered above the body content
};

const FullProductContent = (props: FullProductContentProps) => {
  const {
    product,
    creatorData,
    hardTheme,
    renderCheckoutComp,
    hiddenModalComp,
    aboveBodyComp,
  } = props;
  const { uid } = useSelector(getAccountState).account.accountData;
  const windowWidth = useWindowResize();
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const leftPaneRef = useRef<HTMLDivElement>(null);
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const [desktopStickyTop, setDesktopStickyTop] = useState(0);
  const [isRightContentLong, setIsRightContentLong] = useState(false);
  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth, true),
    [windowWidth]
  );

  useEffect(() => {
    if (!isDesktopSize) {
      setDesktopStickyTop(0);
      setIsRightContentLong(false);
      return;
    }

    const updateLayoutMetrics = () => {
      const paneHeight =
        leftPaneRef.current?.getBoundingClientRect().height ?? 0;
      setDesktopStickyTop(Math.max(0, (window.innerHeight - paneHeight) / 2));
      const rightContentHeight = rightPaneRef.current?.scrollHeight ?? 0;
      setIsRightContentLong(rightContentHeight > 800);
    };

    updateLayoutMetrics();
    window.addEventListener("resize", updateLayoutMetrics);
    const resizeObserver = new ResizeObserver(updateLayoutMetrics);
    if (leftPaneRef.current) {
      resizeObserver.observe(leftPaneRef.current);
    }
    if (rightPaneRef.current) {
      resizeObserver.observe(rightPaneRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateLayoutMetrics);
      resizeObserver.disconnect();
    };
  }, [isDesktopSize]);

  const isHidden = useMemo(() => !!hiddenModalComp, [hiddenModalComp]);

  const isMyAccount = useMemo(
    () => uid === creatorData.uid,
    [uid, creatorData.uid]
  );

  return (
    <div
      className={isDesktopSize ? "AlignedRow" : "ColumnCentering"}
      style={{
        flex: 1,
        minHeight: isHidden ? undefined : "40vh",
        paddingInline: isDesktopSize ? 0 : 14,
        paddingBlock: isDesktopSize ? 40 : 0,
        gap: isDesktop ? 60 : 20,
        marginInline: "auto",
        width: isDesktopSize ? "80%" : undefined,
        justifyContent: "center",
        alignItems: isDesktopSize
          ? isRightContentLong
            ? "flex-start"
            : "center"
          : undefined,
      }}
    >
      <div
        ref={leftPaneRef}
        className="ColumnCentering"
        style={{
          top: isDesktopSize ? desktopStickyTop : undefined,
          position: isDesktopSize ? "sticky" : "relative",
          flexShrink: 0,
          gap: 14,
        }}
      >
        {product.eventType === EventType.PDF ? (
          <PDFViewer productData={product} blurring={isHidden} />
        ) : (
          <MediaPlayer
            productData={product}
            blurring={isHidden}
            selectedMediaIndex={selectedMediaIndex}
            setSelectedMediaIndex={setSelectedMediaIndex}
            creatorName={creatorData.fullName}
            allowAutoplay={!isHidden && !isMyAccount}
            hardTheme={hardTheme}
            renderMediaPreviewCarousel={
              <MediaPreviewCarousel
                event={product}
                hardTheme={hardTheme}
                onSelectMedia={setSelectedMediaIndex}
              />
            }
          />
        )}
        {hiddenModalComp || null}
      </div>
      <div
        ref={rightPaneRef}
        className="ColumnNormal"
        style={{
          gap: isDesktopSize ? 24 : 14,
          width: isDesktop ? 400 : "100%",
          minWidth: isDesktop ? 300 : "100%",
        }}
      >
        <FullProductHeaderSection
          product={product}
          creatorData={creatorData}
          hardTheme={hardTheme}
          contentHidden={isHidden}
          renderCheckoutComp={renderCheckoutComp}
          aboveBodyComp={aboveBodyComp}
        />
      </div>
    </div>
  );
};

export default FullProductContent;
